import {
  Fragment,
  MouseEvent,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { KB_BASE_URL } from '@/shared/config';
// Direct module import: the shared UI barrel pulls the redux store and the
// api layer in, and this renderer needs neither.
import { Skeleton } from '@/shared/ui/Skeleton/Skeleton';

import { i18n } from '../config';
import type {
  KbIrInline,
  KbIrNode,
  KbIrTableCell,
  KbIrTextMark,
  KbIrTextStyles,
} from '../lib';
import { canPreviewDocument } from '../lib/kbDocuments';
import { parseKbLearnHref } from '../lib/kbLinks';
import {
  attachKbVideo,
  closeKbVideo,
  detachKbVideo,
  openKbImage,
  useKbVideoSession,
} from '../model';

import styles from './KnowledgeBase.module.scss';

type OnOpenArticle = (slug: string, market?: string) => void;

// Mark nesting order: bold outermost, then italic, underline, code —
// mirrors the hub renderer's mapping.
const MARK_ORDER: readonly KbIrTextMark[] = [
  'bold',
  'italic',
  'underline',
  'code',
];

const Marked = ({
  text,
  marks,
  styles,
}: {
  text: string;
  marks?: KbIrTextMark[];
  styles?: KbIrTextStyles;
}) => {
  const ordered = MARK_ORDER.filter((mark) => marks?.includes(mark));

  const content = ordered.reduceRight<ReactNode>(
    (child, mark) =>
      mark === 'bold' ? (
        <strong>{child}</strong>
      ) : mark === 'italic' ? (
        <em>{child}</em>
      ) : mark === 'underline' ? (
        <u>{child}</u>
      ) : (
        <code>{child}</code>
      ),
    text,
  );

  // TextState styling (contract @2) wraps the mark nesting.
  if (!styles?.color && !styles?.background) {
    return content;
  }

  return (
    <span
      style={{
        ...(styles.color ? { color: styles.color } : {}),
        ...(styles.background ? { backgroundColor: styles.background } : {}),
      }}
    >
      {content}
    </span>
  );
};

type KbImageNode = Extract<KbIrInline, { type: 'image' }>;

/** Tallest an article picture may be, in px. */
const IMAGE_MAX_HEIGHT = 400;

// Height of the placeholder when the hub does not report the image size.
const PLACEHOLDER_HEIGHT = 220;

/**
 * An image that holds its box while the bytes load.
 *
 * The hub reports the stored file's pixel size, so a block image reserves
 * its exact aspect ratio and the text under it does not jump when the image
 * lands. Without a reported size the box keeps a fixed placeholder height
 * instead. A failed image collapses away — error events do not bubble, so
 * each element carries its own handler.
 */
const KbImage = ({ node, block }: { node: KbImageNode; block?: boolean }) => {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>(
    'loading',
  );

  if (status === 'failed') return null;

  const ratio =
    node.width && node.height ? `${node.width} / ${node.height}` : undefined;

  // The reserved box keeps the picture's ratio, so its width follows from the
  // height cap: the box is never larger than the picture inside it.
  const maxWidth =
    node.width && node.height
      ? `${Math.round(IMAGE_MAX_HEIGHT * (node.width / node.height))}px`
      : undefined;

  // Inline images sit in a sentence at text height: a placeholder there
  // would be bigger than the image itself.
  if (!block) {
    return (
      <img
        className={styles.inlineImage}
        src={node.src}
        alt={node.alt ?? ''}
        loading="lazy"
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('failed')}
      />
    );
  }

  const image = (
    <img
      className={ratio ? styles.mediaImageFill : styles.mediaImage}
      // Without a reserved box the image takes no space until it decodes, so
      // the placeholder holds the space. It stays visible to the loader:
      // `display: none` on a lazy image keeps the request from ever starting,
      // and the picture would stay a skeleton forever.
      style={
        ratio || status === 'loaded' ? undefined : { visibility: 'hidden' }
      }
      src={node.src}
      alt={node.alt ?? ''}
      loading="lazy"
      onLoad={() => setStatus('loaded')}
      onError={() => setStatus('failed')}
    />
  );

  const box = (
    <div
      className={ratio ? styles.imageBox : styles.imageBoxAuto}
      style={ratio ? { aspectRatio: ratio, maxWidth } : undefined}
    >
      {status === 'loading' && (
        <Skeleton
          testID="kb-image-skeleton"
          // In a reserved box the placeholder fills it; without one it holds
          // the space in the flow.
          className={ratio ? styles.imageSkeleton : undefined}
          width="100%"
          height={ratio ? '100%' : PLACEHOLDER_HEIGHT}
          borderRadius={8}
        />
      )}
      {image}
    </div>
  );

  // A block image opens full size. An inline image stays part of the
  // sentence it sits in.
  return (
    <button
      type="button"
      className={styles.imageButton}
      data-component="kb-image-open"
      // The name comes from the alt text, so it survives the image itself
      // being hidden while it loads.
      aria-label={node.alt || undefined}
      onClick={() => openKbImage({ src: node.src, alt: node.alt })}
    >
      {box}
    </button>
  );
};

type KbVideoNode = Extract<KbIrNode, { type: 'video' }>;

/**
 * A video that outlives its article.
 *
 * The player is owned by the app, not by this article: the article only
 * offers the slot. When the article goes away mid-play the same player moves
 * to the app corner and carries on from the same second. A video that was
 * paused, or that reached its end, just goes.
 *
 * While another video plays in the corner, this one waits behind its poster:
 * starting it here closes the corner card and plays this file.
 */
const KbVideo = ({
  node,
  article,
}: {
  node: KbVideoNode;
  article?: { market: string; slug: string };
}) => {
  const intl = useIntl();
  const slotRef = useRef<HTMLDivElement>(null);
  const session = useKbVideoSession();
  const title = node.caption || node.filename || intl.formatMessage(i18n.video);
  // Another video runs in the corner: this one waits for its own play.
  const waiting = Boolean(session?.detached && session.src !== node.src);

  // The slot is where the app's single player sits while this article is on
  // screen. The player element itself belongs to KbVideoHost, so moving it
  // here — or away — never restarts the video.
  useLayoutEffect(() => {
    if (waiting) return;

    attachKbVideo(
      { src: node.src, poster: node.poster, title, article },
      slotRef.current,
    );
  }, [node.src, node.poster, title, article, waiting]);

  // Unmount only: a paused video stays gone, a playing one goes to the corner.
  useLayoutEffect(() => () => detachKbVideo(node.src), [node.src]);

  const startHere = () => {
    // The corner card holds the only player: it has to give way first.
    closeKbVideo();
    attachKbVideo(
      { src: node.src, poster: node.poster, title, article },
      slotRef.current,
      { autoplay: true },
    );
  };

  return (
    <>
      <div ref={slotRef} className={styles.videoSlot} />
      {waiting && (
        <button
          type="button"
          className={styles.videoWaiting}
          aria-label={title}
          onClick={startHere}
        >
          {node.poster && (
            <img
              className={styles.videoWaitingPoster}
              src={node.poster}
              alt=""
            />
          )}
          <span className={styles.videoWaitingPlay} aria-hidden="true">
            {'\u25B6'}
          </span>
        </button>
      )}
    </>
  );
};

const renderInlines = (
  inlines: KbIrInline[],
  onOpenArticle: OnOpenArticle,
): ReactNode =>
  inlines.map((inline, index) => renderInline(inline, index, onOpenArticle));

const renderInline = (
  inline: KbIrInline,
  key: number,
  onOpenArticle: OnOpenArticle,
): ReactNode => {
  switch (inline.type) {
    case 'text':
      return (
        <Marked
          key={key}
          text={inline.text}
          marks={inline.marks}
          styles={inline.styles}
        />
      );
    case 'linebreak':
      return <br key={key} />;
    // A product button name: a pill, never a link or a button.
    case 'button':
      return (
        <span key={key} className={styles.kbButton}>
          {inline.label}
        </span>
      );
    // A step number: open circle for `(3)`, filled square for `[3]`.
    case 'badge':
      return (
        <span
          key={key}
          aria-hidden="true"
          className={
            inline.shape === 'square'
              ? `${styles.kbBadge} ${styles.kbBadgeSquare}`
              : `${styles.kbBadge} ${styles.kbBadgeCircle}`
          }
        >
          {inline.number}
        </span>
      );
    // Inline media (contract @2): compact forms, mirroring the hub's
    // standalone renderer — text-height image, chip links otherwise.
    case 'image':
      return <KbImage key={key} node={inline} />;
    case 'video':
      return (
        <a key={key} className={styles.inlineMediaChip} href={inline.src}>
          {`\u25B6 `}
          {inline.filename || <FormattedMessage {...i18n.video} />}
        </a>
      );
    case 'youtube':
      return (
        <a
          key={key}
          className={styles.inlineMediaChip}
          href={`https://www.youtube.com/watch?v=${inline.videoId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {'\u25B6 YouTube'}
        </a>
      );
    case 'document':
      return (
        <a
          key={key}
          className={styles.inlineMediaChip}
          href={inline.downloadUrl}
        >
          {`\uD83D\uDCC4 ${inline.title || inline.filename || 'Document'}`}
        </a>
      );
    case 'link': {
      const children = renderInlines(inline.children, onOpenArticle);
      const parsed = parseKbLearnHref(inline.href, KB_BASE_URL);

      // The old sanitized path stripped javascript: and other non-web
      // schemes; hub-side validation covers this too, but the guarantee is
      // restored client-side: only http(s) hrefs — plus hub article links
      // checked via parseKbLearnHref — render as anchors. Anything else
      // renders its children as plain text.
      if (!/^https?:\/\//i.test(inline.href) && !parsed) {
        return <Fragment key={key}>{children}</Fragment>;
      }

      // Parity with the old sanitized path: every emitted anchor is a
      // new-tab anchor, regardless of the node's newTab flag.
      if (!parsed) {
        return (
          <a
            key={key}
            href={inline.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      }

      // Hub article links load in the panel; market-level links (no slug)
      // open the hub section in a new tab.
      const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        if (parsed.slug) {
          onOpenArticle(parsed.slug, parsed.market);
          return;
        }

        window.open(
          `${KB_BASE_URL}/learn/${parsed.market}`,
          '_blank',
          'noopener,noreferrer',
        );
      };

      // target/rel keeps a middle-click or modifier-click (which bypasses
      // onClick) from opening the hub with window.opener set.
      return (
        <a
          key={key}
          href={inline.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
        >
          {children}
        </a>
      );
    }
  }
};

const DocumentLinks = ({
  previewUrl,
  downloadUrl,
}: {
  previewUrl?: string;
  downloadUrl: string;
}) => (
  <>
    {previewUrl && (
      <a href={previewUrl} target="_blank" rel="noopener noreferrer">
        <FormattedMessage {...i18n.openDocument} />
      </a>
    )}
    <a href={downloadUrl} download target="_blank" rel="noopener noreferrer">
      <FormattedMessage {...i18n.downloadDocument} />
    </a>
  </>
);

type KbLinkNode = Extract<KbIrNode, { type: 'kb-link' }>;

// Table block (contract @2). Header cells render as th; col/row spans
// pass through; odd rows get a quiet stripe.
const DataTable = ({
  rows,
  onOpenArticle,
}: {
  rows: KbIrTableCell[][];
  onOpenArticle: OnOpenArticle;
}) => (
  <table className={styles.dataTable}>
    <tbody>
      {rows.map((row, rowIndex) => (
        <tr
          key={rowIndex}
          className={rowIndex % 2 === 1 ? styles.dataTableStripe : undefined}
        >
          {row.map((cell, cellIndex) => {
            const Tag = cell.header ? 'th' : 'td';

            return (
              <Tag
                key={cellIndex}
                colSpan={cell.colSpan}
                rowSpan={cell.rowSpan}
              >
                {renderInlines(cell.children, onOpenArticle)}
              </Tag>
            );
          })}
        </tr>
      ))}
    </tbody>
  </table>
);

const KbLinkChip = ({ node }: { node: KbLinkNode }) => (
  // Hub resource links are employees-only surfaces: never intercepted,
  // always a new-tab anchor.
  <a
    className={styles.kbLinkChip}
    href={node.href}
    target="_blank"
    rel="noopener noreferrer"
  >
    <span className={styles.kbLinkChipLabel}>
      <FormattedMessage {...i18n.kbRelated} />
    </span>
    {node.title || node.slug}
  </a>
);

const BlockNode = ({
  node,
  onOpenArticle,
  article,
}: {
  node: KbIrNode;
  onOpenArticle: OnOpenArticle;
  article?: { market: string; slug: string };
}): ReactNode => {
  switch (node.type) {
    case 'paragraph':
      return <p>{renderInlines(node.children, onOpenArticle)}</p>;
    case 'quote':
      // The KB aside: a note, not a quotation. Same look as the hub's
      // standalone article page.
      return (
        <aside className={styles.kbNote}>
          <strong className={styles.kbNoteLabel}>
            <FormattedMessage {...i18n.note} />
          </strong>
          <p>{renderInlines(node.children, onOpenArticle)}</p>
        </aside>
      );
    case 'heading': {
      const Tag = `h${node.level}` as 'h1' | 'h2' | 'h3' | 'h4';
      // A step heading opens with its number badge; the flex layout on the
      // heading aligns the badge to the title's own text box.
      const leadingBadge = node.children[0]?.type === 'badge';

      return (
        <Tag
          id={node.id}
          className={leadingBadge ? styles.headingWithBadge : undefined}
        >
          {renderInlines(node.children, onOpenArticle)}
        </Tag>
      );
    }
    case 'list': {
      const Tag = node.ordered ? 'ol' : 'ul';

      return (
        <Tag>
          {node.items.map((item, index) => (
            <li key={index}>{renderInlines(item, onOpenArticle)}</li>
          ))}
        </Tag>
      );
    }
    case 'table':
      return <DataTable rows={node.rows} onOpenArticle={onOpenArticle} />;
    case 'horizontalrule':
      return <hr />;
    case 'code':
      return (
        <pre>
          <code>{node.text}</code>
        </pre>
      );
    case 'image':
      // Asset captions are intentionally not rendered for now.
      return node.variant === 'block' ? (
        <figure className={styles.mediaFigure}>
          <KbImage node={node} block />
        </figure>
      ) : (
        <KbImage node={node} />
      );
    case 'video':
      if (node.variant === 'block') {
        return (
          <figure className={styles.mediaFigure}>
            <KbVideo node={node} article={article} />
          </figure>
        );
      }

      return <KbVideo node={node} article={article} />;
    case 'youtube':
      return (
        <figure className={styles.mediaFigure}>
          <iframe
            src={`https://www.youtube.com/embed/${node.videoId}`}
            title={node.caption || 'YouTube video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </figure>
      );
    case 'document': {
      // Upload-embedded documents render as a plain paragraph download
      // link; block documents keep the card presentation.
      if (node.variant !== 'block') {
        return (
          <p>
            <a
              href={node.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {node.label || node.title || node.filename}
            </a>
          </p>
        );
      }

      const title = node.title || node.filename;
      // The hub offers a preview URL for every upload, but a type the browser
      // cannot draw (a deck, a sheet) comes back as an attachment. A frame on
      // it downloads the file the moment the article opens.
      const previewUrl = canPreviewDocument(node.mime)
        ? node.previewUrl
        : undefined;

      if (previewUrl) {
        return (
          <figure className={styles.mediaFigure}>
            <iframe
              className={styles.documentFrame}
              src={previewUrl}
              title={title}
              loading="lazy"
            />
            <figcaption className={styles.documentLinksRow}>
              <span className={styles.documentTitle}>{title}</span>
              <DocumentLinks
                previewUrl={previewUrl}
                downloadUrl={node.downloadUrl}
              />
            </figcaption>
          </figure>
        );
      }

      return (
        <figure className={styles.documentCard}>
          <span className={styles.documentTitle}>{title}</span>
          <div className={styles.documentLinksRow}>
            <DocumentLinks downloadUrl={node.downloadUrl} />
          </div>
        </figure>
      );
    }
    case 'kb-link': {
      // Hub resource links are employees-only surfaces: never intercepted,
      // always a new-tab anchor.
      return <KbLinkChip node={node} />;
    }
  }
};

type KbIrRendererProps = {
  nodes: KbIrNode[];
  onOpenArticle: OnOpenArticle;
  /** The article these nodes belong to, so a video can offer a way back. */
  article?: { market: string; slug: string };
};

export const KbIrRenderer = ({
  nodes,
  onOpenArticle,
  article,
}: KbIrRendererProps) => (
  <div className={styles.articleContent}>
    {nodes.map((node, index) => (
      <BlockNode
        key={index}
        node={node}
        onOpenArticle={onOpenArticle}
        article={article}
      />
    ))}
  </div>
);
